/**
 * StatusSection.tsx
 * ステータス: 全ボタン表示、クリックで即保存（フォーム全体は保存しない）
 * 予約経路: セレクト
 */
import React, { useMemo, useCallback } from 'react';
import { SectionProps, StatusOption } from '../types';
import SectionHeader from '../shared/SectionHeader';
import InlineEditField from '../shared/InlineEditField';

const CONTACT_MEDIA_OPTIONS = [
  'lp[ja]', 'lp[en]', 'lp[tw]', 'lp[kr]',
  'JA', 'レンナビ', 'okiren', 'エアトリ', 'rcom',
  '紹介', 'TEL', 'リピート', 'その他',
];

interface StatusSectionProps extends SectionProps {
  reservationStatusList?: StatusOption[];
  /** ステータス即保存（useReservationStatusChange経由） */
  onStatusChange?: (newStatusId: string | number) => void;
  isSavingStatus?: boolean;
}

export default function StatusSection({
  data,
  onChange,
  disabled,
  reservationStatusList = [],
  onStatusChange,
  isSavingStatus,
}: StatusSectionProps) {
  const mediaOptions = useMemo(
    () => CONTACT_MEDIA_OPTIONS.map((o) => ({ label: o, value: o })),
    [],
  );

  const handleStatusClick = useCallback(
    (statusId: string | number) => {
      if (disabled || isSavingStatus) return;
      // 同じステータスを選択した場合は何もしない
      if (String(statusId) === String(data.status)) return;
      onStatusChange?.(statusId);
    },
    [disabled, isSavingStatus, data.status, onStatusChange],
  );

  return (
    <>
      <SectionHeader title="ステータス・ブッキング" />

      {/* ステータスボタン一覧 */}
      <div className="gc-field">
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
          ステータス
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {reservationStatusList.map((status) => {
            const isActive = String(status.id) === String(data.status) || status.code === data.status;
            const bgColor = status.bg_color || '#e2e8f0';
            const textColor = status.text_color || '#475569';

            return (
              <button
                key={String(status.id)}
                type="button"
                onClick={() => handleStatusClick(status.id)}
                disabled={disabled || isSavingStatus}
                style={{
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  border: isActive ? `2px solid ${textColor}` : '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: disabled || isSavingStatus ? 'default' : 'pointer',
                  backgroundColor: isActive ? bgColor : '#f8f8f8',
                  color: isActive ? textColor : '#b0b0b0',
                  opacity: isActive ? 1 : 0.6,
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {status.label || status.name}
              </button>
            );
          })}
        </div>
        {isSavingStatus && (
          <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: 4 }}>保存中...</div>
        )}
      </div>

      <InlineEditField
        label="予約経路"
        field="contact_media"
        value={data.contact_media}
        onChange={onChange}
        disabled={disabled}
        type="select"
        options={mediaOptions}
      />
    </>
  );
}
